import { useUserStore } from '@/modules/user/stores/user.store';
import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { readonly } from 'vue';
import { uniqueRfcService } from '../services/enterprise.service';
import type { Enterprise } from '../types/enterprise.interface';
import type { EnterpriseForm } from '../types/enterpriseForm.interface';

export const useEnterpriseStore = defineStore('enterprise', () => {
  const activeEnterprise = useStorage<Enterprise>('activeEnterprise', null, localStorage, {
    serializer: {
      read: v => JSON.parse(v),
      write: v => JSON.stringify(v),
    },
  });

  /**
   * @author: Omar Sánchez Martinez
   * @since: 18/06/2025
   * @description: Reset personalizado del store de empresa que maneja correctamente useStorage refs
   */
  const resetCustomStore = () => {
    try {
      activeEnterprise.value = null;
    } catch (error) {
      console.warn('Error resetting enterprise store:', error);
    }
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Establece el valor activeEnterprise
   */
  const setActiveEnterprise = (enterpriseData: Enterprise): void => {
    activeEnterprise.value = enterpriseData;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 06/06/2025
   * @description: Actualiza el valor activeEnterprise con los datos del formulario de datos fiscales
   * @param enterpriseFormData - Los datos del formulario de datos fiscales
   */
  const updateActiveEnterprise = (enterpriseFormData: EnterpriseForm): void => {
    if (!activeEnterprise.value) {
      throw new Error('Active enterprise not found');
    }

    // Update active enterprise in enterprise store
    activeEnterprise.value.id = enterpriseFormData.id;
    activeEnterprise.value.razon_social = enterpriseFormData.razon_social;
    activeEnterprise.value.nombre_comercial = enterpriseFormData.nombre_comercial;
    activeEnterprise.value.regimen_fiscal = enterpriseFormData.regimen_fiscal;
    activeEnterprise.value.codigo_postal = enterpriseFormData.codigo_postal;
    activeEnterprise.value.estado = enterpriseFormData.estado;
    activeEnterprise.value.ciudad = enterpriseFormData.ciudad;
    activeEnterprise.value.municipio = enterpriseFormData.municipio;
    activeEnterprise.value.colonia = enterpriseFormData.colonia;
    activeEnterprise.value.calle = enterpriseFormData.calle;
    activeEnterprise.value.numero_exterior = enterpriseFormData.numero_exterior;
    activeEnterprise.value.numero_interior = enterpriseFormData.numero_interior;

    // Also update the enterprise in the user store's enterprises array
    const userStore = useUserStore();
    userStore.updateUserEnterprise(enterpriseFormData.id, activeEnterprise.value);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 09/06/2025
   * @description: Actualiza el valor logo de activeEnterprise
   * @param logoUrl - La URL del logo
   */
  const storeUpdateLogoActiveEnterprise = (logoUrl: string): void => {
    if (!activeEnterprise.value) {
      throw new Error('Active enterprise not found');
    }
    activeEnterprise.value.logo = logoUrl;

    // Also update the logo in the user store's enterprises array
    const userStore = useUserStore();
    userStore.updateUserEnterprise(activeEnterprise.value.id, activeEnterprise.value);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 09/06/2025
   * @description: Elimina el valor logo de activeEnterprise
   */
  const removeLogoActiveEnterprise = (): void => {
    if (!activeEnterprise.value) {
      throw new Error('Active enterprise not found');
    }
    activeEnterprise.value.logo = undefined;

    // Also remove the logo in the user store's enterprises array
    const userStore = useUserStore();
    userStore.updateUserEnterprise(activeEnterprise.value.id, activeEnterprise.value);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Limpia el valor activeEnterprise de localStorage y la reactividad de activeEnterprise
   */
  const clearActiveEnterprise = (): void => {
    //Limpia el valor activeEnterprise de localStorage y la reactividad de activeEnterprise
    activeEnterprise.value = null;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Verifica si el RFC ya está registrado
   */
  const uniqueRfc = async (rfc: string): Promise<boolean> => {
    const response = await uniqueRfcService(rfc);
    return response;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 03/10/2025
   * @description: Actualiza el valor has_ppd_invoices en settings_v40 de activeEnterprise
   */
  const updateHasPpdInvoices = (hasPpdInvoices: boolean): void => {
    if (!activeEnterprise.value) {
      throw new Error('Active enterprise not found');
    }
    activeEnterprise.value.settings_v40.has_ppd_invoices = hasPpdInvoices;

    // Also update the enterprise in the user store's enterprises array
    const userStore = useUserStore();
    userStore.updateUserEnterprise(activeEnterprise.value.id, activeEnterprise.value);
  };

  return {
    // Properties
    activeEnterprise: readonly(activeEnterprise),

    // Actions
    setActiveEnterprise,
    clearActiveEnterprise,
    uniqueRfc,
    updateActiveEnterprise,
    storeUpdateLogoActiveEnterprise,
    removeLogoActiveEnterprise,
    resetCustomStore,
    updateHasPpdInvoices,
  };
});
